import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { EXCLUDE_REFERENTI } from '../utils/contactPollution';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { User } from '@supabase/supabase-js';
import { QueryExecutionService } from '../services/queryExecutionService';
import { InsightsGenerationService } from '../services/insightsGenerationService';
import { ReportSchedulingService } from '../services/reportSchedulingService';
import { ExportService } from '../services/exportService';
import type { 
  CustomReport, 
  ReportExecution, 
  AnalyticsDashboard, 
  DashboardWidget, 
  DataInsight, 
  AnalyticsKPI, 
  KPIValue,
  CustomReportFormData,
  AnalyticsDashboardFormData,
  DashboardWidgetFormData,
  AnalyticsKPIFormData,
  CustomReportsFilter,
  AnalyticsDashboardsFilter,
  DataInsightsFilter,
  AnalyticsKPIsFilter,
  ReportQueryConfig,
  ChartConfig,
  ScheduleConfig
} from '../types';

interface AnalyticsData {
  revenueByMonth: {
    month: string;
    amount: number;
  }[];
  clientsByMonth: {
    month: string;
    count: number;
  }[];
  projectStatusCounts: {
    status: string;
    count: number;
  }[];
  dealStatusCounts: {
    status: string;
    count: number;
  }[];
  topClients: {
    id: string;
    business_name: string;
    total_revenue: number;
  }[];
}

interface AnalyticsStore {
  // Original analytics data
  data: AnalyticsData;
  loading: boolean;
  error: string | null;
  fetchAnalytics: () => Promise<void>;
  
  // Dashboard deal data for all components
  dashboardDeals: any[];
  dashboardDealsLoading: boolean;
  dashboardDealsError: string | null;
  dashboardDealsTimestamp: number | null;
  
  // Flag per evitare chiamate doppie
  isLoadingDashboardDeals: boolean;

  // Function to fetch dashboard deal data
  fetchDashboardDeals: (user: User | null, forceRefresh?: boolean) => Promise<any[]>;
  
  // Function to reset all data
  resetAll: () => void;
  
  // Advanced Analytics - Reports state
  reports: CustomReport[];
  currentReport: CustomReport | null;
  reportExecutions: ReportExecution[];
  loadingReports: boolean;
  
  // Dashboards state
  dashboards: AnalyticsDashboard[];
  currentDashboard: AnalyticsDashboard | null;
  dashboardWidgets: DashboardWidget[];
  loadingDashboards: boolean;
  
  // Insights state
  insights: DataInsight[];
  loadingInsights: boolean;
  
  // KPIs state
  kpis: AnalyticsKPI[];
  kpiValues: KPIValue[];
  loadingKPIs: boolean;
  
  // Custom Reports actions
  fetchReports: (filter?: CustomReportsFilter) => Promise<void>;
  fetchReportById: (id: string) => Promise<CustomReport | null>;
  createReport: (data: CustomReportFormData) => Promise<CustomReport>;
  updateReport: (id: string, data: Partial<CustomReportFormData>) => Promise<CustomReport>;
  deleteReport: (id: string) => Promise<boolean>;
  executeReport: (id: string, parameters?: Record<string, any>) => Promise<ReportExecution>;
  scheduleReport: (id: string, schedule: ScheduleConfig) => Promise<void>;
  exportReport: (executionId: string, format: 'pdf' | 'excel' | 'csv') => Promise<string>;
  
  // Report Executions actions
  fetchReportExecutions: (reportId?: string) => Promise<void>;
  getExecutionResult: (executionId: string) => Promise<any>;
  cancelExecution: (executionId: string) => Promise<void>;
  
  // Dashboards actions
  fetchDashboards: (filter?: AnalyticsDashboardsFilter) => Promise<void>;
  fetchDashboardById: (id: string) => Promise<AnalyticsDashboard | null>;
  createDashboard: (data: AnalyticsDashboardFormData) => Promise<AnalyticsDashboard>;
  updateDashboard: (id: string, data: Partial<AnalyticsDashboardFormData>) => Promise<AnalyticsDashboard>;
  deleteDashboard: (id: string) => Promise<boolean>;
  setDefaultDashboard: (id: string) => Promise<void>;
  
  // Dashboard Widgets actions
  fetchDashboardWidgets: (dashboardId: string) => Promise<void>;
  createWidget: (dashboardId: string, data: DashboardWidgetFormData) => Promise<DashboardWidget>;
  updateWidget: (id: string, data: Partial<DashboardWidgetFormData>) => Promise<DashboardWidget>;
  deleteWidget: (id: string) => Promise<boolean>;
  updateWidgetPosition: (id: string, x: number, y: number, width: number, height: number) => Promise<void>;
  refreshWidget: (id: string) => Promise<any>;
  
  // Data Insights actions
  fetchInsights: (filter?: DataInsightsFilter) => Promise<void>;
  generateInsights: () => Promise<void>;
  dismissInsight: (id: string) => Promise<void>;
  markInsightAsRead: (id: string) => Promise<void>;
  
  // KPIs actions
  fetchKPIs: (filter?: AnalyticsKPIsFilter) => Promise<void>;
  fetchKPIById: (id: string) => Promise<AnalyticsKPI | null>;
  createKPI: (data: AnalyticsKPIFormData) => Promise<AnalyticsKPI>;
  updateKPI: (id: string, data: Partial<AnalyticsKPIFormData>) => Promise<AnalyticsKPI>;
  deleteKPI: (id: string) => Promise<boolean>;
  calculateKPI: (id: string) => Promise<KPIValue>;
  fetchKPIValues: (kpiId: string, limit?: number) => Promise<void>;
  
  // Query Builder actions
  validateQuery: (config: ReportQueryConfig) => Promise<{ valid: boolean; error?: string }>;
  previewQuery: (config: ReportQueryConfig) => Promise<any[]>;
  getAvailableFields: (dataSource: string) => Promise<{ name: string; type: string; description?: string }[]>;
  getAvailableDataSources: () => Promise<{ name: string; description: string; tables: string[] }[]>;
  
  // Utility actions
  clearError: () => void;
  setCurrentReport: (report: CustomReport | null) => void;
  setCurrentDashboard: (dashboard: AnalyticsDashboard | null) => void;
}

const initialData: AnalyticsData = {
  revenueByMonth: [],
  clientsByMonth: [],
  projectStatusCounts: [],
  dealStatusCounts: [],
  topClients: [],
};

// Cache expiration time in ms (5 minuti)
const CACHE_EXPIRATION = 5 * 60 * 1000;

// Function to check if cache is still valid
const isCacheValid = (timestamp: number | null): boolean => {
  if (!timestamp) return false;
  const now = Date.now();
  return now - timestamp < CACHE_EXPIRATION;
};

// Get the current month date range
const getCurrentMonthDateRange = () => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();
  
  // Build date range for the current month
  const startDate = `${currentYear}-${currentMonth.toString().padStart(2, '0')}-01`;
  const endDate = currentMonth === 12 
    ? `${currentYear + 1}-01-01`
    : `${currentYear}-${(currentMonth + 1).toString().padStart(2, '0')}-01`;
  
  return { startDate, endDate, currentMonth, currentYear };
};

export const useAnalyticsStore = create<AnalyticsStore>((set, get) => ({
  // Original analytics data
  data: initialData,
  loading: false,
  error: null,
  
  // Dashboard deals data
  dashboardDeals: [],
  dashboardDealsLoading: false,
  dashboardDealsError: null,
  dashboardDealsTimestamp: null,
  isLoadingDashboardDeals: false,
  
  // Advanced Analytics initial state
  reports: [],
  currentReport: null,
  reportExecutions: [],
  loadingReports: false,
  dashboards: [],
  currentDashboard: null,
  dashboardWidgets: [],
  loadingDashboards: false,
  insights: [],
  loadingInsights: false,
  kpis: [],
  kpiValues: [],
  loadingKPIs: false,

  fetchAnalytics: async () => {
    set({ loading: true, error: null });
    try {
      // Get last 6 months
      const months = Array.from({ length: 6 }, (_, i) => {
        const date = subMonths(new Date(), i);
        return {
          month: format(date, 'MMM yyyy'),
          startDate: startOfMonth(date).toISOString(),
          endDate: endOfMonth(date).toISOString(),
        };
      }).reverse();

      // Revenue by month (from invoices)
      const { data: invoiceData, error: invoiceError } = await supabase
        .from('invoices')
        .select('amount, created_at')
        .order('created_at', { ascending: false });

      if (invoiceError) throw invoiceError;

      const revenueByMonth = months.map(({ month, startDate, endDate }) => {
        const monthlyInvoices = invoiceData?.filter(
          (invoice) => invoice.created_at >= startDate && invoice.created_at <= endDate
        ) || [];
        
        const amount = monthlyInvoices.reduce((sum, invoice) => sum + Number(invoice.amount), 0);
        
        return { month, amount };
      });

      // Clients by month
      const { data: clientData, error: clientError } = await supabase
        .from('clients')
        .select('created_at')
        .or(EXCLUDE_REFERENTI) // Fase 2: no persone-referente nel conteggio clienti/mese
        .order('created_at', { ascending: false });

      if (clientError) throw clientError;

      const clientsByMonth = months.map(({ month, startDate, endDate }) => {
        const count = clientData?.filter(
          (client) => client.created_at >= startDate && client.created_at <= endDate
        ).length || 0;
        
        return { month, count };
      });

      // Project status counts
      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .select('status');

      if (projectError) throw projectError;

      const projectStatusMap: Record<string, number> = {};
      projectData?.forEach((project) => {
        projectStatusMap[project.status] = (projectStatusMap[project.status] || 0) + 1;
      });

      const projectStatusCounts = Object.entries(projectStatusMap).map(([status, count]) => ({
        status,
        count,
      }));

      // Deal status counts
      const { data: dealData, error: dealError } = await supabase
        .from('deals')
        .select('status');

      if (dealError) throw dealError;

      const dealStatusMap: Record<string, number> = {};
      dealData?.forEach((deal) => {
        dealStatusMap[deal.status] = (dealStatusMap[deal.status] || 0) + 1;
      });

      const dealStatusCounts = Object.entries(dealStatusMap).map(([status, count]) => ({
        status,
        count,
      }));

      // Top clients by revenue
      const { data: topClientsData, error: topClientsError } = await supabase
        .from('invoices')
        .select(`
          amount,
          deal:deal_id (
            client_id,
            client:clients (
              id,
              business_name
            )
          )
        `);

      if (topClientsError) throw topClientsError;

      const clientRevenueMap: Record<string, { id: string; business_name: string; total_revenue: number }> = {};
      
      topClientsData?.forEach((invoice) => {
        if (invoice.deal?.client) {
          const clientId = invoice.deal.client.id;
          if (!clientRevenueMap[clientId]) {
            clientRevenueMap[clientId] = {
              id: clientId,
              business_name: invoice.deal.client.business_name,
              total_revenue: 0,
            };
          }
          clientRevenueMap[clientId].total_revenue += Number(invoice.amount);
        }
      });

      const topClients = Object.values(clientRevenueMap)
        .sort((a, b) => b.total_revenue - a.total_revenue)
        .slice(0, 5);

      set({
        data: {
          revenueByMonth,
          clientsByMonth,
          projectStatusCounts,
          dealStatusCounts,
          topClients,
        },
        loading: false,
      });
    } catch (error) {
      set({ error: friendlyDbError(error), loading: false });
    }
  },
  
  // Fetch all deals for dashboard components
  fetchDashboardDeals: async (user, forceRefresh = false) => {
    const state = get();
    
    // Check if already loading to prevent duplicate calls
    if (state.isLoadingDashboardDeals) {
      console.log('[AnalyticsStore] Already loading dashboard deals, skipping duplicate call');
      return state.dashboardDeals;
    }
    
    // Check cache validity if not forcing refresh
    if (
      !forceRefresh && 
      state.dashboardDeals.length > 0 && 
      isCacheValid(state.dashboardDealsTimestamp)
    ) {
      console.log('[AnalyticsStore] Using cached dashboard deals data');
      return state.dashboardDeals;
    }
    
    // Prevent multiple calls
    set({ isLoadingDashboardDeals: true });
    
    try {
      set({ dashboardDealsLoading: true, dashboardDealsError: null });
      
      if (!user) {
        throw new Error('Utente non autenticato');
      }
      
      // Get date range for current month
      const { startDate, endDate, currentMonth, currentYear } = getCurrentMonthDateRange();
      
      // Get user profile for organization
      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', user.id)
        .single();
        
      if (!profileData?.organization_id) {
        throw new Error('Utente senza organizzazione');
      }
      
      const organizationId = profileData.organization_id;
      
      console.log(`[AnalyticsStore] Fetching deals for organization ${organizationId}, month ${currentMonth}/${currentYear}`);
      
      // Fetch organization's deals with client info
      const { data: deals, error: dealsError } = await supabase
        .from('deals')
        .select(`
          id, 
          amount, 
          status, 
          closed_at, 
          created_at, 
          title, 
          assigned_to,
          client:clients(id, business_name, email, phone)
        `)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });
      
      if (dealsError) throw dealsError;
      
      set({ 
        dashboardDeals: deals || [], 
        dashboardDealsLoading: false,
        dashboardDealsTimestamp: Date.now(),
        isLoadingDashboardDeals: false
      });
      
      console.log(`[AnalyticsStore] Loaded ${deals?.length || 0} deals for dashboard`);
      return deals || [];
      
    } catch (error) {
      console.error('[AnalyticsStore] Error fetching dashboard deals:', error);
      set({ 
        dashboardDealsLoading: false, 
        dashboardDealsError: friendlyDbError(error),
        isLoadingDashboardDeals: false
      });
      return [];
    }
  },
  
  // Reset all data
  resetAll: () => {
    set({
      data: initialData,
      loading: false,
      error: null,
      dashboardDeals: [],
      dashboardDealsLoading: false,
      dashboardDealsError: null,
      dashboardDealsTimestamp: null,
      isLoadingDashboardDeals: false,
      // Reset advanced analytics state
      reports: [],
      currentReport: null,
      reportExecutions: [],
      loadingReports: false,
      dashboards: [],
      currentDashboard: null,
      dashboardWidgets: [],
      loadingDashboards: false,
      insights: [],
      loadingInsights: false,
      kpis: [],
      kpiValues: [],
      loadingKPIs: false
    });
  },

  // =====================================================
  // ADVANCED ANALYTICS IMPLEMENTATIONS
  // =====================================================

  // Custom Reports actions
  fetchReports: async (filter) => {
    set({ loadingReports: true, error: null });
    
    try {
      let query = supabase
        .from('custom_reports')
        .select(`
          *,
          created_by_user:user_profiles!created_by(id, email, full_name),
          recent_executions:report_executions(
            id, executed_at, status, execution_time_ms, rows_returned
          )
        `)
        .order('updated_at', { ascending: false });

      // Apply filters
      if (filter?.search) {
        query = query.or(`name.ilike.%${filter.search}%,description.ilike.%${filter.search}%`);
      }
      if (filter?.category) {
        query = query.eq('category', filter.category);
      }
      if (filter?.created_by) {
        query = query.eq('created_by', filter.created_by);
      }
      if (filter?.is_public !== undefined) {
        query = query.eq('is_public', filter.is_public);
      }
      if (filter?.is_scheduled !== undefined) {
        query = query.eq('is_scheduled', filter.is_scheduled);
      }
      if (filter?.tags && filter.tags.length > 0) {
        query = query.overlaps('tags', filter.tags);
      }
      if (filter?.data_sources && filter.data_sources.length > 0) {
        query = query.overlaps('data_sources', filter.data_sources);
      }

      const { data, error } = await query;

      if (error) throw error;

      set({ reports: data || [], loadingReports: false });
    } catch (error) {
      console.error('Error fetching reports:', error);
      set({ error: friendlyDbError(error), loadingReports: false });
    }
  },

  fetchReportById: async (id) => {
    try {
      const { data, error } = await supabase
        .from('custom_reports')
        .select(`
          *,
          created_by_user:user_profiles!created_by(id, email, full_name),
          recent_executions:report_executions(
            id, executed_at, status, execution_time_ms, rows_returned, result_summary
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error fetching report:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  createReport: async (data) => {
    try {
      const { data: report, error } = await supabase
        .from('custom_reports')
        .insert([{
          ...data,
          created_by: (await supabase.auth.getUser()).data.user?.id
        }])
        .select(`
          *,
          created_by_user:user_profiles!created_by(id, email, full_name)
        `)
        .single();

      if (error) throw error;

      set((state) => ({
        reports: [report, ...state.reports]
      }));

      return report;
    } catch (error) {
      console.error('Error creating report:', error);
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  updateReport: async (id, data) => {
    try {
      const { data: report, error } = await supabase
        .from('custom_reports')
        .update({
          ...data,
          updated_by: (await supabase.auth.getUser()).data.user?.id
        })
        .eq('id', id)
        .select(`
          *,
          created_by_user:user_profiles!created_by(id, email, full_name)
        `)
        .single();

      if (error) throw error;

      set((state) => ({
        reports: state.reports.map(r => r.id === id ? report : r),
        currentReport: state.currentReport?.id === id ? report : state.currentReport
      }));

      return report;
    } catch (error) {
      console.error('Error updating report:', error);
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  deleteReport: async (id) => {
    try {
      const { error } = await supabase
        .from('custom_reports')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        reports: state.reports.filter(r => r.id !== id),
        currentReport: state.currentReport?.id === id ? null : state.currentReport
      }));

      return true;
    } catch (error) {
      console.error('Error deleting report:', error);
      set({ error: friendlyDbError(error) });
      return false;
    }
  },

  executeReport: async (id, parameters) => {
    try {
      const report = await get().fetchReportById(id);
      if (!report) {
        throw new Error('Report not found');
      }

      const result = await QueryExecutionService.executeQuery(
        report.query_config,
        id,
        'manual'
      );

      // Fetch updated executions
      get().fetchReportExecutions(id);

      return {
        id: result.execution_id,
        result_data: result.data,
        execution_time_ms: result.execution_time_ms,
        rows_returned: result.rows_count,
        status: result.success ? 'completed' : 'failed',
        error_message: result.error_message
      };
    } catch (error) {
      console.error('Error executing report:', error);
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  scheduleReport: async (id, schedule) => {
    try {
      await ReportSchedulingService.scheduleReport(id, schedule);

      // Refresh the report
      const updatedReport = await get().fetchReportById(id);
      if (updatedReport) {
        set((state) => ({
          reports: state.reports.map(r => r.id === id ? updatedReport : r)
        }));
      }
    } catch (error) {
      console.error('Error scheduling report:', error);
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  exportReport: async (executionId, format) => {
    try {
      const service = new ExportService();
      const result = await service.exportExecution(executionId, format);
      return result.download_url;
    } catch (error) {
      console.error('Error exporting report:', error);
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  // Report Executions actions
  fetchReportExecutions: async (reportId) => {
    try {
      let query = supabase
        .from('report_executions')
        .select(`
          *,
          executed_by_user:user_profiles!executed_by(id, email, full_name),
          report:custom_reports(id, name)
        `)
        .order('executed_at', { ascending: false })
        .limit(50);

      if (reportId) {
        query = query.eq('report_id', reportId);
      }

      const { data, error } = await query;

      if (error) throw error;

      set({ reportExecutions: data || [] });
    } catch (error) {
      console.error('Error fetching report executions:', error);
      set({ error: friendlyDbError(error) });
    }
  },

  getExecutionResult: async (executionId) => {
    try {
      const { data, error } = await supabase
        .from('report_executions')
        .select('result_data, result_summary')
        .eq('id', executionId)
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error getting execution result:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  cancelExecution: async (executionId) => {
    try {
      const { error } = await supabase
        .from('report_executions')
        .update({ status: 'cancelled' })
        .eq('id', executionId);

      if (error) throw error;

      // Refresh executions
      get().fetchReportExecutions();
    } catch (error) {
      console.error('Error cancelling execution:', error);
      set({ error: friendlyDbError(error) });
    }
  },

  // Dashboards actions
  fetchDashboards: async (filter) => {
    set({ loadingDashboards: true, error: null });
    
    try {
      let query = supabase
        .from('analytics_dashboards')
        .select(`
          *,
          created_by_user:user_profiles!created_by(id, email, full_name),
          widgets:dashboard_widgets(*)
        `)
        .order('updated_at', { ascending: false });

      // Apply filters
      if (filter?.search) {
        query = query.or(`name.ilike.%${filter.search}%,description.ilike.%${filter.search}%`);
      }
      if (filter?.created_by) {
        query = query.eq('created_by', filter.created_by);
      }
      if (filter?.is_default !== undefined) {
        query = query.eq('is_default', filter.is_default);
      }

      const { data, error } = await query;

      if (error) throw error;

      set({ dashboards: data || [], loadingDashboards: false });
    } catch (error) {
      console.error('Error fetching dashboards:', error);
      set({ error: friendlyDbError(error), loadingDashboards: false });
    }
  },

  fetchDashboardById: async (id) => {
    try {
      const { data, error } = await supabase
        .from('analytics_dashboards')
        .select(`
          *,
          created_by_user:user_profiles!created_by(id, email, full_name),
          widgets:dashboard_widgets(
            *,
            report:custom_reports(id, name, query_config)
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  createDashboard: async (data) => {
    try {
      const { data: dashboard, error } = await supabase
        .from('analytics_dashboards')
        .insert([{
          ...data,
          created_by: (await supabase.auth.getUser()).data.user?.id
        }])
        .select(`
          *,
          created_by_user:user_profiles!created_by(id, email, full_name)
        `)
        .single();

      if (error) throw error;

      set((state) => ({
        dashboards: [dashboard, ...state.dashboards]
      }));

      return dashboard;
    } catch (error) {
      console.error('Error creating dashboard:', error);
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  updateDashboard: async (id, data) => {
    try {
      const { data: dashboard, error } = await supabase
        .from('analytics_dashboards')
        .update({
          ...data,
          updated_by: (await supabase.auth.getUser()).data.user?.id
        })
        .eq('id', id)
        .select(`
          *,
          created_by_user:user_profiles!created_by(id, email, full_name)
        `)
        .single();

      if (error) throw error;

      set((state) => ({
        dashboards: state.dashboards.map(d => d.id === id ? dashboard : d),
        currentDashboard: state.currentDashboard?.id === id ? dashboard : state.currentDashboard
      }));

      return dashboard;
    } catch (error) {
      console.error('Error updating dashboard:', error);
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  deleteDashboard: async (id) => {
    try {
      const { error } = await supabase
        .from('analytics_dashboards')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        dashboards: state.dashboards.filter(d => d.id !== id),
        currentDashboard: state.currentDashboard?.id === id ? null : state.currentDashboard
      }));

      return true;
    } catch (error) {
      console.error('Error deleting dashboard:', error);
      set({ error: friendlyDbError(error) });
      return false;
    }
  },

  setDefaultDashboard: async (id) => {
    try {
      // First, unset all other default dashboards
      await supabase
        .from('analytics_dashboards')
        .update({ is_default: false })
        .neq('id', id);

      // Then set the new default
      const { error } = await supabase
        .from('analytics_dashboards')
        .update({ is_default: true })
        .eq('id', id);

      if (error) throw error;

      // Refresh dashboards
      get().fetchDashboards();
    } catch (error) {
      console.error('Error setting default dashboard:', error);
      set({ error: friendlyDbError(error) });
    }
  },

  // Dashboard Widgets actions
  fetchDashboardWidgets: async (dashboardId) => {
    try {
      const { data, error } = await supabase
        .from('dashboard_widgets')
        .select(`
          *,
          report:custom_reports(id, name, query_config)
        `)
        .eq('dashboard_id', dashboardId)
        .order('position_y')
        .order('position_x');

      if (error) throw error;

      set({ dashboardWidgets: data || [] });
    } catch (error) {
      console.error('Error fetching dashboard widgets:', error);
      set({ error: friendlyDbError(error) });
    }
  },

  createWidget: async (dashboardId, data) => {
    try {
      const { data: widget, error } = await supabase
        .from('dashboard_widgets')
        .insert([{
          ...data,
          dashboard_id: dashboardId
        }])
        .select(`
          *,
          report:custom_reports(id, name, query_config)
        `)
        .single();

      if (error) throw error;

      set((state) => ({
        dashboardWidgets: [...state.dashboardWidgets, widget]
      }));

      return widget;
    } catch (error) {
      console.error('Error creating widget:', error);
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  updateWidget: async (id, data) => {
    try {
      const { data: widget, error } = await supabase
        .from('dashboard_widgets')
        .update(data)
        .eq('id', id)
        .select(`
          *,
          report:custom_reports(id, name, query_config)
        `)
        .single();

      if (error) throw error;

      set((state) => ({
        dashboardWidgets: state.dashboardWidgets.map(w => w.id === id ? widget : w)
      }));

      return widget;
    } catch (error) {
      console.error('Error updating widget:', error);
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  deleteWidget: async (id) => {
    try {
      const { error } = await supabase
        .from('dashboard_widgets')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        dashboardWidgets: state.dashboardWidgets.filter(w => w.id !== id)
      }));

      return true;
    } catch (error) {
      console.error('Error deleting widget:', error);
      set({ error: friendlyDbError(error) });
      return false;
    }
  },

  updateWidgetPosition: async (id, x, y, width, height) => {
    try {
      const { error } = await supabase
        .from('dashboard_widgets')
        .update({
          position_x: x,
          position_y: y,
          width,
          height
        })
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        dashboardWidgets: state.dashboardWidgets.map(w => 
          w.id === id 
            ? { ...w, position_x: x, position_y: y, width, height }
            : w
        )
      }));
    } catch (error) {
      console.error('Error updating widget position:', error);
      set({ error: friendlyDbError(error) });
    }
  },

  refreshWidget: async (id) => {
    try {
      const widget = get().dashboardWidgets.find(w => w.id === id);
      if (!widget) return null;

      // If widget has a report, execute it
      if (widget.report_id) {
        const execution = await get().executeReport(widget.report_id);
        return execution.result_data;
      }

      return null;
    } catch (error) {
      console.error('Error refreshing widget:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  // Data Insights actions
  fetchInsights: async (filter) => {
    set({ loadingInsights: true, error: null });
    
    try {
      let query = supabase
        .from('data_insights')
        .select(`
          *,
          dismissed_by_user:user_profiles!dismissed_by(id, email, full_name)
        `)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filter?.insight_type) {
        query = query.eq('insight_type', filter.insight_type);
      }
      if (filter?.priority) {
        query = query.eq('priority', filter.priority);
      }
      if (filter?.status) {
        query = query.eq('status', filter.status);
      }
      if (filter?.is_read !== undefined) {
        query = query.eq('is_read', filter.is_read);
      }
      if (filter?.data_source) {
        query = query.eq('data_source', filter.data_source);
      }
      if (filter?.date_from) {
        query = query.gte('period_start', filter.date_from);
      }
      if (filter?.date_to) {
        query = query.lte('period_end', filter.date_to);
      }

      const { data, error } = await query;

      if (error) throw error;

      set({ insights: data || [], loadingInsights: false });
    } catch (error) {
      console.error('Error fetching insights:', error);
      set({ error: friendlyDbError(error), loadingInsights: false });
    }
  },

  generateInsights: async () => {
    try {
      await InsightsGenerationService.generateInsights({
        dashboard_id: null, // Generate general insights
        data_source: 'all',
        insight_types: ['trend', 'anomaly', 'correlation'],
        priority_threshold: 'medium'
      });

      // Refresh insights after generation
      get().fetchInsights();
    } catch (error) {
      console.error('Error generating insights:', error);
      set({ error: friendlyDbError(error) });
    }
  },

  dismissInsight: async (id) => {
    try {
      const { error } = await supabase
        .from('data_insights')
        .update({
          status: 'dismissed',
          dismissed_by: (await supabase.auth.getUser()).data.user?.id,
          dismissed_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        insights: state.insights.map(i => 
          i.id === id 
            ? { ...i, status: 'dismissed' as const, dismissed_at: new Date().toISOString() }
            : i
        )
      }));
    } catch (error) {
      console.error('Error dismissing insight:', error);
      set({ error: friendlyDbError(error) });
    }
  },

  markInsightAsRead: async (id) => {
    try {
      const { error } = await supabase
        .from('data_insights')
        .update({ is_read: true })
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        insights: state.insights.map(i => 
          i.id === id ? { ...i, is_read: true } : i
        )
      }));
    } catch (error) {
      console.error('Error marking insight as read:', error);
      set({ error: friendlyDbError(error) });
    }
  },

  // KPIs actions
  fetchKPIs: async (filter) => {
    set({ loadingKPIs: true, error: null });
    
    try {
      let query = supabase
        .from('analytics_kpis')
        .select(`
          *,
          created_by_user:user_profiles!created_by(id, email, full_name),
          current_value:kpi_values(
            id, value, target_value, recorded_at, metadata
          )
        `)
        .order('updated_at', { ascending: false });

      // Apply filters
      if (filter?.search) {
        query = query.or(`name.ilike.%${filter.search}%,description.ilike.%${filter.search}%`);
      }
      if (filter?.category) {
        query = query.eq('category', filter.category);
      }
      if (filter?.is_active !== undefined) {
        query = query.eq('is_active', filter.is_active);
      }
      if (filter?.created_by) {
        query = query.eq('created_by', filter.created_by);
      }
      if (filter?.update_frequency) {
        query = query.eq('update_frequency', filter.update_frequency);
      }

      const { data, error } = await query;

      if (error) throw error;

      set({ kpis: data || [], loadingKPIs: false });
    } catch (error) {
      console.error('Error fetching KPIs:', error);
      set({ error: friendlyDbError(error), loadingKPIs: false });
    }
  },

  fetchKPIById: async (id) => {
    try {
      const { data, error } = await supabase
        .from('analytics_kpis')
        .select(`
          *,
          created_by_user:user_profiles!created_by(id, email, full_name),
          historical_values:kpi_values(
            id, value, target_value, recorded_at, metadata
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error fetching KPI:', error);
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  createKPI: async (data) => {
    try {
      const { data: kpi, error } = await supabase
        .from('analytics_kpis')
        .insert([{
          ...data,
          created_by: (await supabase.auth.getUser()).data.user?.id
        }])
        .select(`
          *,
          created_by_user:user_profiles!created_by(id, email, full_name)
        `)
        .single();

      if (error) throw error;

      set((state) => ({
        kpis: [kpi, ...state.kpis]
      }));

      return kpi;
    } catch (error) {
      console.error('Error creating KPI:', error);
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  updateKPI: async (id, data) => {
    try {
      const { data: kpi, error } = await supabase
        .from('analytics_kpis')
        .update(data)
        .eq('id', id)
        .select(`
          *,
          created_by_user:user_profiles!created_by(id, email, full_name)
        `)
        .single();

      if (error) throw error;

      set((state) => ({
        kpis: state.kpis.map(k => k.id === id ? kpi : k)
      }));

      return kpi;
    } catch (error) {
      console.error('Error updating KPI:', error);
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  deleteKPI: async (id) => {
    try {
      const { error } = await supabase
        .from('analytics_kpis')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        kpis: state.kpis.filter(k => k.id !== id)
      }));

      return true;
    } catch (error) {
      console.error('Error deleting KPI:', error);
      set({ error: friendlyDbError(error) });
      return false;
    }
  },

  calculateKPI: async (id) => {
    try {
      // ATTENZIONE, da non "aggiustare" cambiando nome alla RPC.
      // `calculate_kpi_value` non esiste in produzione: questo metodo lancia sempre.
      // Non ha chiamanti (e' dichiarato nell'interfaccia ma nessun componente lo usa),
      // quindi oggi non rompe nulla.
      // In produzione esiste `calculate_kpi_values` (plurale), ma NON e' la stessa cosa:
      // e' un abbozzo che restituisce numeri inventati e fissi nel codice — 75 offerte,
      // 125000 di valore, 150 clienti — con tanto di commento "in production you'd query
      // actual data". Puntarci questo metodo scriverebbe quei numeri in `kpi_values` e
      // li mostrerebbe come KPI reali: peggio dell'errore attuale.
      // Il calcolo vero dei KPI e' da scrivere; finche' non c'e', questo resta rotto
      // in modo rumoroso e visibile.
      const { data, error } = await supabase.rpc('calculate_kpi_value', {
        kpi_id_param: id
      });

      if (error) throw error;

      // Refresh KPI values
      get().fetchKPIValues(id, 1);

      return data;
    } catch (error) {
      console.error('Error calculating KPI:', error);
      set({ error: friendlyDbError(error) });
      throw error;
    }
  },

  fetchKPIValues: async (kpiId, limit = 50) => {
    try {
      const { data, error } = await supabase
        .from('kpi_values')
        .select(`
          *,
          kpi:analytics_kpis(id, name, data_type, display_format, unit)
        `)
        .eq('kpi_id', kpiId)
        .order('recorded_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      set({ kpiValues: data || [] });
    } catch (error) {
      console.error('Error fetching KPI values:', error);
      set({ error: friendlyDbError(error) });
    }
  },

  // Query Builder actions
  validateQuery: async (config) => {
    try {
      const result = await QueryExecutionService.validateQuery(config);
      return result;
    } catch (error) {
      return { valid: false, error: friendlyDbError(error) };
    }
  },

  previewQuery: async (config) => {
    try {
      const result = await QueryExecutionService.previewQuery(config);
      return result.data || [];
    } catch (error) {
      console.error('Error previewing query:', error);
      set({ error: friendlyDbError(error) });
      return [];
    }
  },

  getAvailableFields: async (dataSource) => {
    try {
      const result = await QueryExecutionService.getAvailableFields(dataSource);
      return result;
    } catch (error) {
      console.error('Error getting available fields:', error);
      set({ error: friendlyDbError(error) });
      return [];
    }
  },

  getAvailableDataSources: async () => {
    try {
      const result = await QueryExecutionService.getDataSources();
      return result;
    } catch (error) {
      console.error('Error getting available data sources:', error);
      set({ error: friendlyDbError(error) });
      return [];
    }
  },

  // Utility actions
  clearError: () => set({ error: null }),
  
  setCurrentReport: (report) => set({ currentReport: report }),
  
  setCurrentDashboard: (dashboard) => set({ currentDashboard: dashboard })
}));